import { ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withStyles } from 'vopay';
import { BodyText, Button, CopyButton, MaterialSymbol, Title } from 'UI';
import useIntegrationGroupLabels from './useIntegrationGroupLabels';
import { IntegrationCardData } from './types';
import styles from './IntegrationCard.module.scss';

const cn = withStyles(styles);

type IntegrationCardProps = {
  integration: IntegrationCardData;
  onActionClick: () => void;
};

const ACTION_ICONS: { [state in IntegrationCardData['state']]?: ComponentProps<typeof MaterialSymbol>['icon'] } = {
  available: 'settings',
  locked: 'lock',
};

export default function IntegrationCard({ integration, onActionClick }: IntegrationCardProps) {
  const { id, name, group, logo, state, description, prerequisites } = integration;
  const { t } = useTranslation('translations', { keyPrefix: 'Integrations.IntegrationCard' });
  // Catalogue copy resolves against a root t with full keys (see types.ts)
  const { t: tRoot } = useTranslation();
  const groupLabels = useIntegrationGroupLabels();
  const isConnected = state === 'connected';
  const [isHovered, setIsHovered] = useState(false);
  // Tapping the body flips the card on touch devices, where there is no hover. On a pointer device
  // the card is already flipped by the hover, so the tap pins the opposite face instead — that way
  // the click has a visible effect where it happens. The pin is dropped when the pointer leaves,
  // so a card is never left face-down after the user has moved on.
  const [pinnedFace, setPinnedFace] = useState<'front' | 'back'>();
  const isFlipped = pinnedFace ? pinnedFace === 'back' : isHovered;

  function toggleFace() {
    setPinnedFace(isFlipped ? 'front' : 'back');
  }

  function getActionLabel() {
    switch (state) {
      case 'connected':
        return t('updateDetails', { defaultValue: 'Update Details' });
      case 'locked':
        return group === 'accountingAndErp'
          ? t('unlockService', { defaultValue: 'Unlock Service' })
          : t('unlockIntegration', { defaultValue: 'Unlock Integration' });
      default:
        return t('configure', { defaultValue: 'Configure' });
    }
  }

  return (
    <article
      className={cn('card', isFlipped && 'flipped')}
      data-testid={`integration-card-${id}`}
      data-state={state}
      data-flipped={isFlipped}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setPinnedFace(undefined);
      }}
    >
      <div className={cn('faces')} onClick={toggleFace}>
        <div className={cn('face', 'front')} aria-hidden={isFlipped}>
          <div className={cn('logoRow')}>
            <img className={cn('logo')} src={logo} alt="" />

            {isConnected && (
              <span className={cn('connectedTag')}>
                <MaterialSymbol icon="check_circle" size="14px" />
                {t('connected', { defaultValue: 'Connected' })}
              </span>
            )}
          </div>

          <Title level="5" className={cn('name')}>
            {name}
          </Title>

          <BodyText size="sm" className={cn('group')}>
            {groupLabels[group]}
          </BodyText>

          <BodyText className={cn('description')}>{description(tRoot)}</BodyText>
        </div>

        <div className={cn('face', 'back')} aria-hidden={!isFlipped}>
          <BodyText weight="em" className={cn('backTitle')}>
            {t('whatYouNeed', { defaultValue: 'What you need:' })}
          </BodyText>

          <ul className={cn('prerequisites')}>
            {prerequisites.map(({ id: prerequisiteId, label, example }) => (
              <li key={prerequisiteId} className={cn('prerequisite')}>
                <MaterialSymbol icon="check_circle" size="18px" className={cn('checkIcon')} />

                <div>
                  <BodyText>{label(tRoot)}</BodyText>

                  {example && (
                    <div className={cn('example')} onClick={(event) => event.stopPropagation()}>
                      <span className={cn('exampleText')}>{example}</span>
                      <CopyButton content={example} size="16px" />
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <footer className={cn('footer')}>
        <Button
          className={cn('action')}
          colorScheme="primary"
          size="small"
          iconLeft={ACTION_ICONS[state]}
          onClick={onActionClick}
        >
          {getActionLabel()}
        </Button>
      </footer>
    </article>
  );
}
